import { ArrowRightLeft, Plane, CreditCard, Shield, FileText, Headphones, Check, DollarSign, Ticket, Car } from "lucide-react";

const services = [
  {
    icon: ArrowRightLeft,
    title: "Currency Exchange",
    description: "Real-time exchange rates for 45+ currencies with transparent pricing and instant conversions.",
    features: ["Live market rates", "Zero hidden fees", "Instant conversion"],
    color: "blue"
  },
  {
    icon: Plane,
    title: "Holiday Packages",
    description: "Curated travel experiences to amazing destinations worldwide with competitive pricing.",
    features: ["100+ destinations", "Customizable packages", "24/7 support"],
    color: "green"
  },
  {
    icon: CreditCard,
    title: "Forex Cards",
    description: "Multi-currency prepaid cards for safe and convenient international travel.",
    features: ["Multi-currency support", "Global acceptance", "Secure transactions"],
    color: "yellow"
  },
  {
    icon: Shield,
    title: "Travel Insurance",
    description: "Comprehensive coverage for peace of mind during your travels abroad.",
    features: ["Medical coverage", "Trip cancellation", "Baggage protection"],
    color: "blue"
  },
  {
    icon: FileText,
    title: "Visa Services",
    description: "Hassle-free visa processing and documentation support for all countries.",
    features: ["Document assistance", "Fast processing", "Expert guidance"],
    color: "green"
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    description: "Round-the-clock customer support for all your travel and forex needs.",
    features: ["Phone support", "Live chat", "Emergency assistance"],
    color: "yellow"
  },
  {
    icon: DollarSign,
    title: "Remittance (Student)",
    description: "Secure money transfer services specifically designed for students studying abroad.",
    features: ["Student-friendly rates", "Fast transfers", "Education compliance"],
    color: "blue"
  },
  {
    icon: Ticket,
    title: "Ticketing (Domestic & International)",
    description: "Comprehensive flight booking services for both domestic and international travel.",
    features: ["Best price guarantee", "Flexible booking", "24/7 booking support"],
    color: "green"
  },
  {
    icon: Car,
    title: "Car/Cruise/Hotel Booking",
    description: "Complete accommodation and transportation booking services for seamless travel experiences.",
    features: ["Car rentals worldwide", "Cruise packages", "Hotel reservations"],
    color: "yellow"
  }
];

const getColorClasses = (color: string) => {
  switch (color) {
    case 'blue':
      return {
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        hoverBg: 'group-hover:bg-blue-200'
      };
    case 'green':
      return {
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
        hoverBg: 'group-hover:bg-green-200'
      };
    case 'yellow':
      return {
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        hoverBg: 'group-hover:bg-yellow-200'
      };
    default:
      return {
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-600',
        hoverBg: 'group-hover:bg-gray-200'
      };
  }
};

export default function ServicesSection() {
  return (
    <section id="services" className="py-20 bg-gradient-to-b from-tw-purple-light to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-tw-orange-light text-tw-orange rounded-full text-sm font-medium mb-4">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Our Premium Services
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Complete <span className="text-tw-orange">Travel Solutions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive travel and forex solutions tailored to meet all your international business and leisure needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const colorClasses = getColorClasses(service.color);
            const IconComponent = service.icon;

            return (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 hover:border-tw-orange hover:shadow-2xl transition-all duration-300 group">
                <div className={`w-16 h-16 ${colorClasses.iconBg} ${colorClasses.hoverBg} rounded-2xl flex items-center justify-center mb-6 transition-colors group-hover:scale-110 transform duration-300`}>
                  <IconComponent className={`w-8 h-8 ${colorClasses.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed text-sm">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}